import {
  configureStore,
  ConfigureStoreOptions,
  createListenerMiddleware,
  DevToolsEnhancerOptions,
  Middleware,
  Reducer,
  Store,
  StoreEnhancer,
} from '@reduxjs/toolkit';

import { browserStorage, PersistConfig, persistListener, readPersisted } from './persist';

/** Untyped on purpose: an app types it with `.withTypes<RootState, AppDispatch>()`. */
export type StartListening = ReturnType<typeof createListenerMiddleware>['startListening'];

/** A hand-written thunk as `S` runs it. RTK's `ThunkAction` lacks the listener middleware's `dispatch` overload. */
export type StoreThunk<S extends Store, R = void> = (dispatch: S['dispatch'], getState: S['getState']) => R;

type PreloadedState<R> = R extends Reducer<any, any, infer P> ? P : never;

/** RTK's options for its own middleware: `thunk`, `immutableCheck`, `serializableCheck`, `actionCreatorCheck`. */
export type DefaultMiddlewareOptions = Parameters<Parameters<NonNullable<ConfigureStoreOptions['middleware']>>[0]>[0];

export interface DevToolsOptions extends DevToolsEnhancerOptions {
  /** The localStorage key a browser sets to `'1'` to turn DevTools on in production. Defaults to `redux_devtools`. */
  optInKey?: string;
}

export interface AppStoreOptions<R extends Reducer> {
  reducer: R;
  /** Persisted fields go over it. */
  preloadedState?: PreloadedState<R>;
  /** Runs after RTK's own middleware. */
  middleware?: Middleware[];
  defaultMiddleware?: DefaultMiddlewareOptions;
  enhancers?: StoreEnhancer[];
  listeners?: ((startListening: StartListening) => void)[];
  persist?: PersistConfig<ReturnType<R>>;
  /** A boolean forces it. Otherwise on in development, and in production only where the browser opted in. */
  devTools?: boolean | DevToolsOptions;
}

/** Off in production unless this browser opted in, since state can hold tokens. */
const devToolsSetting = (devTools: boolean | DevToolsOptions = {}): boolean | DevToolsEnhancerOptions => {
  if (typeof devTools === 'boolean') return devTools;

  const { optInKey = 'redux_devtools', ...options } = devTools;
  if (process.env.NODE_ENV !== 'production') return options;
  try {
    return browserStorage()?.getItem(optInKey) === '1' && options;
  } catch {
    return false;
  }
};

/** Each call has its own listener middleware, so a store made per SSR request shares nothing. */
export function createAppStore<R extends Reducer>({
  reducer,
  preloadedState,
  middleware = [],
  defaultMiddleware,
  enhancers = [],
  listeners = [],
  persist,
  devTools,
}: AppStoreOptions<R>) {
  const listener = createListenerMiddleware();

  const store = configureStore({
    reducer,
    preloadedState: persist ? readPersisted(reducer, persist, preloadedState) : preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware(defaultMiddleware).prepend(listener.middleware).concat(middleware),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(enhancers),
    devTools: devToolsSetting(devTools),
  });

  if (persist) persistListener(persist)(listener.startListening);
  listeners.forEach((add) => add(listener.startListening));

  return store;
}
